import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import PropTypes from 'prop-types';
import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {Trans, useTranslation} from 'react-i18next';
import {useAuth} from '@apps-common/auth-react';

import CopyButton from '@explorer/components/ui/copy-button';
import MetadataTable from './table.jsx';
import ThumbnailImage from '@explorer/components/ui/thumbnail-image';
import {getUrl as getThumbUrl} from '@explorer/util/thumbs';
import {trackEvent} from '@explorer/analytics.js';
import {withDarkTheme} from '@explorer/styles';

const MetadataModal = props => {
  const {t} = useTranslation();
  const auth = useAuth();

  return (
    <Dialog
      fullScreen
      open={props.open}
      onClose={props.onClose}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 0,
          boxShadow: 'none',
          backgroundImage: 'none',
          backgroundColor: 'rgba(0, 31, 35, 0.90)',
        },
      }}
      data-cy="metadata-overlay"
    >
      <React.Fragment>
        <AppBar position="sticky" color="transparent" elevation={0}>
          <Box
            minHeight={48}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box position="absolute" top={0.5} right={0.5}>
              <Tooltip
                title={t('metadataModal.closeButtonTooltip')}
                placement="left"
              >
                <IconButton onClick={props.onClose}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography color="textPrimary" align="center">
              <Trans
                i18nKey="metadataModal.messageExit"
                components={{
                  exitLink: <Link underline="always" onClick={props.onClose} />,
                }}
              />
            </Typography>
          </Box>
        </AppBar>

        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Container disableGutters>
            <Grid container spacing={4} justifyContent="center">
              <Grid item xs={6} sm={5} container alignItems="center">
                <Box my={1} width="100%">
                  <Typography
                    color="textPrimary"
                    display="inline"
                    marginRight={0.5}
                  >
                    {t('common:date.med', {
                      date: props.item.properties.acquired,
                    })}
                  </Typography>
                  <Typography variant="caption" display="inline">
                    {t('common:time.long', {
                      date: props.item.properties.acquired,
                    })}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography
                      color="textPrimary"
                      noWrap
                      style={{marginRight: 8}}
                    >
                      {props.item.properties.item_id || props.item.id}
                    </Typography>
                    <CopyButton
                      textToCopy={
                        props.item.properties.item_id || props.item.id
                      }
                      onCopy={() =>
                        trackEvent('Metadata Modal: Copied scene id', {
                          sceneId: props.item.id,
                          hostedData: Boolean(
                            props.item.properties.hosted_metadata
                          ),
                        })
                      }
                      title={t('metadataModal.buttonCopyScene')}
                    />
                  </Box>
                </Box>
                <MetadataTable item={props.item} />
              </Grid>
              <Grid
                item
                xs={6}
                sm={7}
                container
                justifyContent="center"
                alignItems="center"
              >
                <ThumbnailImage
                  src={getThumbUrl({
                    item: props.item,
                    size: 512,
                    token: auth.user?.accessToken,
                  })}
                  maxWidth={512}
                  maxHeight={512}
                  errorSize="large"
                />
              </Grid>
            </Grid>
          </Container>
        </DialogContent>
      </React.Fragment>
    </Dialog>
  );
};

MetadataModal.propTypes = {
  item: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  open: PropTypes.bool,
};

export default withDarkTheme(MetadataModal);
